<script setup>
import { router } from '@inertiajs/vue3';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import FilePondField from '@js/components/fields/FilePondField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { onMounted, reactive, ref, watch } from 'vue';
import { array, mixed, object, string } from 'yup';

const { ticketStatuses, ticketPriorities, ticketThemes, tenant, tenants } =
    defineProps({
        ticketStatuses: {
            required: true,
            type: Array,
        },
        ticketPriorities: {
            required: true,
            type: Array,
        },
        ticketThemes: {
            required: true,
            type: Array,
        },
        tenant: {
            type: Object,
            required: false,
        },
        tenants: {
            required: true,
            type: Array,
        },
    });

const { route } = useRoute();

const isSubmitting = ref(false);

const tenantUsers = ref(null);

const users = ref(null);

const loading = ref(true);

const attachmentsField = ref(null);

const attachmentsUploading = ref(false);

const form = reactive({
    name: '',
    message: '',
    priority: ticketPriorities[1],
    tenant: tenant
        ? tenants.find((innerTenant) => innerTenant.id === tenant.id)
        : tenants[0],
    theme: null,
    isBillable: false,
    linkedTenantUsers: [],
    users: [],
    status: ticketStatuses.find(
        (status) => status.value === 'pending_customer_response',
    ),
    attachments: [],
});

const validationSchema = object({
    tenant: object().required('Le client est requis'),
    theme: object().nullable(),
    isBillable: mixed().nullable(),
    name: string().required('Le nom est requis'),
    message: string().required('Le message est requis'),
    priority: object().required('La priorité est requise'),
    linkedTenantUsers: array().of(object()).nullable(),
    users: array().of(object()).nullable(),
    attachments: mixed().nullable(),
    status: object().required('Le statut est requis'),
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.getTenantUsers', { tenant: form.tenant.id })),
        axios.get(route('front.projectManagement.task.getTicketApikomUsers')),
    ])
        .then((response) => {
            tenantUsers.value = response[0].data;
            users.value = response[1].data;

            form.linkedTenantUsers = [];
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function submit() {
    isSubmitting.value = true;

    let data = new FormData();

    data.append('name', form.name);
    data.append('message', form.message);
    data.append('priority', form.priority.value);
    data.append('tenantId', form.tenant.id);
    data.append('theme', form.theme ? form.theme.value : '');
    data.append('isBillable', form.isBillable ? '1' : '0');
    if (form.linkedTenantUsers && form.linkedTenantUsers.length > 0) {
        form.linkedTenantUsers.forEach((user) => {
            data.append('linkedTenantUserIds[]', user.id);
        });
    }
    if (form.users && form.users.length > 0) {
        form.users.forEach((user) => {
            data.append('userIds[]', user.id);
        });
    }
    data.append('status', form.status.value);

    form.attachments.forEach((token) => {
        data.append('attachments[]', token);
    });

    axios
        .post(route('front.projectManagement.task.postTicket'), data)
        .then(() => {
            toastSaved();

            goBack();
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.projectManagement.task.tickets'));
        });
}

onMounted(() => {
    updateData();
});

watch(
    () => form.tenant,
    () => {
        updateData();
    },
);
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
        :loading="loading"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ComboboxField
                        v-model="form.tenant"
                        :options="tenants"
                        label="Client"
                        field-name="tenant"
                        field-id="tenant"
                        filteredOptionKey="name"
                        required
                        :disabled="!!tenant"
                    >
                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ComboboxField>

                    <ListboxField
                        v-model="form.theme"
                        field-id="theme"
                        field-name="theme"
                        label="Thème"
                        :options="ticketThemes"
                        has-empty-option
                    >
                        <template #buttonContent>
                            {{ form.theme.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <SwitchField
                        v-model="form.isBillable"
                        field-id="isBillable"
                        field-name="isBillable"
                        label="Facturable"
                    />
                </FormGrid>

                <InputField
                    v-model="form.name"
                    label="Nom"
                    field-name="name"
                    field-id="name"
                    :max="138 - form.tenant.name.length"
                    type="text"
                    required
                >
                    <template #inputPrefix>
                        {{ form.tenant.name }} [CLIENT]
                        {{ form.theme ? '[' + form.theme.label + ']' : '' }}
                        -
                    </template>
                </InputField>
            </FormContent>

            <FormContent>
                <ComboboxField
                    v-if="tenantUsers"
                    v-model="form.linkedTenantUsers"
                    :options="tenantUsers"
                    label="Utilisateurs clients liés"
                    legend="Permet de lier des utilisateurs clients à ce ticket. Ils recevront des notifications liées à ce ticket."
                    field-name="linkedTenantUsers"
                    field-id="linkedTenantUsers"
                    filteredOptionKey="fullName"
                    has-empty-option
                    multiple
                >
                    <template #optionContent="{ option }">
                        {{ option.fullName }} - {{ option.email }}
                    </template>
                </ComboboxField>
            </FormContent>

            <FormContent>
                <ComboboxField
                    v-model="form.users"
                    :options="users"
                    label="Responsable(s)"
                    field-name="users"
                    field-id="users"
                    filteredOptionKey="fullName"
                    has-empty-option
                    multiple
                >
                    <template #optionContent="{ option }">
                        {{ option.fullName }}
                    </template>
                </ComboboxField>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.priority"
                        field-id="priority"
                        field-name="priority"
                        label="Priorité"
                        :options="ticketPriorities"
                        required
                    >
                        <template #buttonContent>
                            {{ form.priority.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-model="form.status"
                        field-id="status"
                        field-name="status"
                        label="Statut"
                        :options="ticketStatuses"
                        required
                    >
                        <template #buttonContent>
                            {{ form.status.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <EditorField
                    v-model="form.message"
                    label="Message"
                    field-name="message"
                    field-id="message"
                    size="small"
                    required
                    disable-html-toggle
                />

                <FilePondField
                    ref="attachmentsField"
                    v-model="form.attachments"
                    label="Pièces jointes"
                    field-name="attachments"
                    field-id="attachments"
                    accept="image/png, image/jpeg, image/jpg, application/pdf, text/plain, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
                    @processing="attachmentsUploading = $event"
                />
            </FormContent>
        </FormColumn>

        <FormFooter
            :is-submitting="isSubmitting"
            :errors="errors"
            :disabled="attachmentsUploading"
        />
    </FormRoot>
</template>
